
import type { ApolloQueryResult } from 'apollo-client'
import type { FetchResult } from 'apollo-link'
import { defineComponent } from 'vue'

import CreateBillingPortalSessionMutation from '~/apollo/mutations/owner/createBillingPortalSession.gql'
import DetachSavedCardMutation from '~/apollo/mutations/owner/detachSavedCard.gql'
import SetDefaultCardMutation from '~/apollo/mutations/owner/setDefaultCard.gql'
import UnifiedBillingPauseInfoQuery from '~/apollo/queries/owner/unifiedBillingPauseInfo.gql'

import ActiveUserMixin, { type DashboardContext } from '~/mixins/activeUserMixin'
import OwnerDetailMixin from '~/mixins/ownerDetailMixin'
import { TeamPerms } from '~/types/permTypes'
import {
  InvoiceStatus,
  type CreateBillingPortalSessionMutation as CreateBillingPortalSessionMutationData,
  type DetachSavedCardMutation as DetachSavedCardMutationData,
  type SetDefaultCardMutation as SetDefaultCardMutationData,
  type UnifiedBillingPauseInfoQuery as UnifiedBillingPauseInfoQueryData
} from '~/types/types'
import {
  mapUnifiedCardToPaymentMethod,
  mapUnifiedInvoiceToView,
  type InvoiceView,
  type PaymentMethodView
} from '~/utils/billing'

const pauseImagePng = require('~/assets/images/ui-states/subscription-paused.png')
const pauseImageWebp = require('~/assets/images/ui-states/subscription-paused.webp')

type PauseSubscription = NonNullable<
  NonNullable<UnifiedBillingPauseInfoQueryData['owner']>['productSubscription']
>

export default defineComponent({
  name: 'SubscriptionPastDueState',

  mixins: [ActiveUserMixin, OwnerDetailMixin],

  data() {
    return {
      pauseImagePng,
      pauseImageWebp,
      loading: true,
      subscription: null as PauseSubscription | null,
      cardToDelete: null as string | null,
      deletingCard: false,
      openingPortal: false,
      settingDefaultCard: false
    }
  },

  computed: {
    isAdmin(): boolean {
      const ctx = this.activeDashboardContext as DashboardContext
      return (
        ctx?.type !== 'user' &&
        this.$gateKeeper.team(TeamPerms.UPDATE_BILLING_DETAILS, this.teamPerms.permission)
      )
    },
    billingDetails(): PauseSubscription['billingDetails'] | null {
      return this.subscription?.billingDetails ?? null
    },
    paymentMethods(): PaymentMethodView[] {
      const cards = this.billingDetails?.cards
      if (!cards?.length) return []
      return cards
        .filter((c): c is NonNullable<typeof c> => Boolean(c?.endingIn))
        .map(mapUnifiedCardToPaymentMethod)
    },
    pastDueInvoices(): InvoiceView[] {
      const invoices = this.billingDetails?.invoices
      if (!invoices?.length) return []
      // "Past due" = the actionable, still-owed states. Open invoices await payment;
      // Uncollectible ones the payment processor gave up on. Paid/Draft/Void are not
      // amounts the user can or needs to settle here, so they're excluded.
      return invoices
        .filter(
          (inv): inv is NonNullable<typeof inv> =>
            Boolean(inv) &&
            (inv?.status === InvoiceStatus.Open || inv?.status === InvoiceStatus.Uncollectible)
        )
        .map(mapUnifiedInvoiceToView)
    },
    retryUrl(): string {
      // Any past-due invoice's hosted-payment URL settles the balance, so use the first
      // one that has a URL to drive the "Retry payment" button.
      return this.pastDueInvoices.find((inv) => inv.url)?.url ?? ''
    },
    cardToDeleteDetails(): PaymentMethodView | null {
      if (!this.cardToDelete) return null
      return this.paymentMethods.find((m) => m.id === this.cardToDelete) ?? null
    }
  },

  // Fetch billing details from `mounted()` (not `fetch`/`created`) so the query runs
  // client-side only — Nuxt 2 never invokes `mounted()` during SSR. The query may be
  // slow, and the pause screen must not block server-side rendering of the dashboard.
  async mounted() {
    await this.fetchBillingInfo()
  },

  methods: {
    async fetchBillingInfo(): Promise<void> {
      const { owner, provider } = this.$route.params
      if (!owner || !provider) {
        this.loading = false
        return
      }
      try {
        const response: ApolloQueryResult<UnifiedBillingPauseInfoQueryData> =
          await this.$fetchGraphqlData(
            UnifiedBillingPauseInfoQuery,
            { login: owner, provider: this.$providerMetaMap[provider].value },
            true
          )
        this.subscription = response.data?.owner?.productSubscription ?? null
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while loading your billing details. Please try again later.'
        )
      } finally {
        this.loading = false
      }
    },

    addCreditCard(): void {
      this.openBillingPortal()
    },

    async openBillingPortal(): Promise<void> {
      if (this.openingPortal || !this.owner?.id) return
      this.openingPortal = true
      try {
        const result = (await this.$applyGraphqlMutation(CreateBillingPortalSessionMutation, {
          ownerId: this.owner.id
        })) as FetchResult<CreateBillingPortalSessionMutationData>
        const url = result.data?.createBillingPortalSession?.url
        if (url) {
          window.location.href = url
          return
        }
        this.openingPortal = false
        this.$logErrorAndToast(
          new Error('createBillingPortalSession returned no url'),
          'We could not open the billing portal right now. Please try again later.'
        )
      } catch (error) {
        this.openingPortal = false
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while opening the billing portal. Please try again later.'
        )
      }
    },

    retryPayment(): void {
      if (!this.retryUrl) return
      window.open(this.retryUrl, '_blank', 'noopener,noreferrer')
    },

    async detachSavedCard(): Promise<void> {
      if (!this.cardToDelete || !this.owner?.id) return
      this.deletingCard = true
      try {
        const result = (await this.$applyGraphqlMutation(DetachSavedCardMutation, {
          ownerId: this.owner.id,
          paymentMethodId: this.cardToDelete
        })) as FetchResult<DetachSavedCardMutationData>
        if (!result.data?.detachSavedCard?.ok) {
          this.$logErrorAndToast(
            new Error('detachSavedCard returned ok=false'),
            'We could not remove the card. Please try again later.'
          )
          return
        }
        this.cardToDelete = null
        this.$toast.success('Card removed successfully.')
        await this.fetchBillingInfo()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while removing the card. Please try again later.'
        )
      } finally {
        this.deletingCard = false
      }
    },

    async setDefaultCard(paymentMethodId: string): Promise<void> {
      // Guard against concurrent updates: overlapping mutations + refetches can resolve
      // out of order and leave the UI showing a default that contradicts the backend.
      if (!this.owner?.id || this.settingDefaultCard) return
      this.settingDefaultCard = true
      try {
        const result = (await this.$applyGraphqlMutation(SetDefaultCardMutation, {
          ownerId: this.owner.id,
          paymentMethodId
        })) as FetchResult<SetDefaultCardMutationData>
        if (!result.data?.setDefaultCard?.ok) {
          this.$logErrorAndToast(
            new Error('setDefaultCard returned ok=false'),
            'We could not update the default card. Please try again later.'
          )
          return
        }
        this.$toast.success('Default card updated.')
        await this.fetchBillingInfo()
      } catch (error) {
        this.$logErrorAndToast(
          error as Error,
          'We encountered an error while updating the default card. Please try again later.'
        )
      } finally {
        this.settingDefaultCard = false
      }
    }
  }
})
